import { useEffect, useState } from "react";

/**
 * useOrientation — detekcja orientacji urządzenia.
 *
 * Zwraca:
 *   - orientation: "portrait" | "landscape"
 *   - isPhoneLandscape: telefon w orientacji poziomej
 *       (landscape + max-height 500px), czyli układ pracy „obrócony telefon”.
 *   - isPhoneLandscapeWide: landscape + max-height 600px + max-width 1279px
 *       (obejmuje też duże telefony/małe tablety w poziomie).
 *
 * Efekt uboczny: ustawia atrybut `data-orientation` na <body>, dzięki czemu
 * globalne style CSS w `index.css` mogą zmieniać layout bez propagowania
 * propa przez wszystkie komponenty.
 *
 * Ten hook nie wprowadza własnego stanu do reduxa, kontekstu ani localStorage.
 * Aktywacja jest w pełni automatyczna — bez przełącznika.
 */
export const useOrientation = () => {
  const getState = () => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return { orientation: "portrait", isPhoneLandscape: false, isPhoneLandscapeWide: false };
    }
    const landscape = window.matchMedia("(orientation: landscape)").matches;
    const phoneLandscape = window.matchMedia(
      "(orientation: landscape) and (max-height: 500px)"
    ).matches;
    const phoneLandscapeWide = window.matchMedia(
      "(orientation: landscape) and (max-height: 600px) and (max-width: 1279px)"
    ).matches;
    return {
      orientation: landscape ? "landscape" : "portrait",
      isPhoneLandscape: phoneLandscape,
      isPhoneLandscapeWide: phoneLandscapeWide || phoneLandscape,
    };
  };

  const [state, setState] = useState(getState);

  useEffect(() => {
    const mqLandscape = window.matchMedia("(orientation: landscape)");
    const mqPhone = window.matchMedia("(orientation: landscape) and (max-height: 500px)");
    const mqWide = window.matchMedia(
      "(orientation: landscape) and (max-height: 600px) and (max-width: 1279px)"
    );
    const handler = () => setState(getState());
    // Nowsze API + fallback dla starych Safari.
    mqLandscape.addEventListener?.("change", handler) || mqLandscape.addListener?.(handler);
    mqPhone.addEventListener?.("change", handler) || mqPhone.addListener?.(handler);
    mqWide.addEventListener?.("change", handler) || mqWide.addListener?.(handler);
    return () => {
      mqLandscape.removeEventListener?.("change", handler) || mqLandscape.removeListener?.(handler);
      mqPhone.removeEventListener?.("change", handler) || mqPhone.removeListener?.(handler);
      mqWide.removeEventListener?.("change", handler) || mqWide.removeListener?.(handler);
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const body = document.body;
    if (!body) return;
    // "phone-landscape" — telefon w poziomie (główny tryb 2-kolumnowy).
    // "phone-landscape-wide" — obejmuje też małe tablety w poziomie.
    // "portrait" / "landscape" — informacja pomocnicza.
    const value = state.isPhoneLandscape
      ? "phone-landscape"
      : state.isPhoneLandscapeWide
        ? "phone-landscape-wide"
        : state.orientation;
    body.setAttribute("data-orientation", value);
    return () => {
      // Nie usuwamy atrybutu — jeden globalny stan; kolejny render zaktualizuje wartość.
    };
  }, [state]);

  return state;
};

export default useOrientation;
